const get = require('lodash/get');

function formatPrice(amount) {
	if (amount == null) return { value: null, text: null };
	const num = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
	return { value: num, text: num.toFixed(2) };
}

function cleanStringForUrl(str) {
	let cleanStr = str.replace(/[ ]+/g, '-');
	cleanStr = cleanStr.replace(/[^a-z0-9-]/gi, '');
	return cleanStr.replace(/-+/g, '-');
}

function buildFacilityUrl(id, name, city, state) {
	if (!city || !state || !name) {
		return `/facility/${id}`;
	}
	const cityState = `${cleanStringForUrl(`${city}-${state}`)}-self-storage`;
	const facilityStr = cleanStringForUrl(`${name}-${id}`);
	return `/${cityState}/${facilityStr}.html`;
}

function buildReserveUrl(id, name, city, state, unitId) {
	const facilityUrl = buildFacilityUrl(id, name, city, state);
	if (!unitId || facilityUrl.startsWith('/facility/')) {
		return facilityUrl;
	}
	return `${facilityUrl.replace(/\.html$/, '')}/reserve/${unitId}`;
}

function buildStateUrl(fullState) {
	if (!fullState) {
		return '';
	}
	return `/${cleanStringForUrl(fullState)}-storage.html`.toLowerCase();
}

function buildCityUrl(city, state) {
	if (!city || !state) {
		return '';
	}
	return `/${cleanStringForUrl(`${city}-${state}`)}-self-storage.html`;
}

function pickDrilldownLinks(...candidates) {
	const populated = candidates.find(
		(dl) =>
			dl &&
			((dl.amenities && dl.amenities.length) ||
				(dl.unitTypes && dl.unitTypes.length) ||
				(dl.vehicleStorage && dl.vehicleStorage.length)),
	);
	return populated || candidates.find(Boolean) || {};
}

/**
 * Transforms SSAG's flat search response into the shape that
 * formatSearchFacilities / normalizr / reducers expect.
 */
function adaptSSAGSearch(data) {
	if (!data) return data;

	// Already in expected shape — pass through
	if (data.search && data.search.facilityCollection) {
		return data;
	}

	// Not an SSAG response — pass through
	if (!data.listings && !data.searchId && !data.totalListings) {
		return data;
	}

	const listings = (data.listings || []).map((listing) => {
		const adapted = { ...listing };

		const seo = adapted.seoLocation || {};
		adapted.url = {
			facility: buildFacilityUrl(
				adapted.id,
				adapted.name,
				seo.city,
				seo.state,
			),
			state: buildStateUrl(seo.fullState),
			city: buildCityUrl(seo.city, seo.state),
			website: adapted.website || '',
		};

		if (adapted.units && !Array.isArray(adapted.units)) {
			const unitArr = [];
			const seen = new Set();
			let recommended = null;
			['smallUnit', 'mediumUnit', 'largeUnit', 'recommendedUnit'].forEach(
				(key) => {
					const unit = adapted.units[key];
					if (!unit || !unit.id) {
						return;
					}
					if (seen.has(unit.id)) {
						if (key === 'recommendedUnit') {
							recommended =
								unitArr.find((u) => u.id === unit.id) ||
								recommended;
						}
						return;
					}
					seen.add(unit.id);
					const normalizedUnit = { ...unit };
					if (
						unit.price &&
						typeof unit.price === 'object' &&
						!unit.price.text
					) {
						const displayPrice =
							unit.price.sparefoot || unit.price.meta || 0;
						const regularPrice =
							unit.price.regular || displayPrice;
						normalizedUnit.price = formatPrice(displayPrice);
						normalizedUnit.regularPrice =
							formatPrice(regularPrice);
					}
					if (normalizedUnit.promotion == null) {
						normalizedUnit.promotion = unit.special || '';
					}
					if (
						!normalizedUnit.unitTypeText &&
						normalizedUnit.typeText
					) {
						normalizedUnit.unitTypeText =
							normalizedUnit.typeText;
					}
					if (
						!normalizedUnit.amenitiesList &&
						Array.isArray(unit.availableAmenities)
					) {
						normalizedUnit.amenitiesList =
							unit.availableAmenities.join(', ');
					}
					// Unit cards (BookableUnitDetails, BookableUnitDetailsV2)
					// render amenitiesText, not amenitiesList -- SSAG only
					// sends the latter, so without this the amenity line is
					// blank on every unit card.
					if (
						!normalizedUnit.amenitiesText &&
						Array.isArray(unit.availableAmenities)
					) {
						normalizedUnit.amenitiesText =
							unit.availableAmenities.join(', ');
					}
					normalizedUnit.url = buildReserveUrl(
						listing.id,
						listing.name,
						(listing.seoLocation || {}).city,
						(listing.seoLocation || {}).state,
						unit.id,
					);
					unitArr.push(normalizedUnit);
					if (key === 'recommendedUnit') {
						recommended = normalizedUnit;
					}
				},
			);
			adapted.units = unitArr;
			adapted.recommendedUnit = recommended || unitArr[0] || null;
		}

		// Reviews: SSAG { average, number } → { average, count, collection, pros, cons }
		if (adapted.reviews) {
			adapted.reviews = {
				average: adapted.reviews.average || 0,
				count: adapted.reviews.number || adapted.reviews.count || 0,
				collection: adapted.reviews.collection || [],
				pros: adapted.reviews.pros || {},
				cons: adapted.reviews.cons || {},
			};
		}

		// Fees: SSAG flat adminFee/lockFee/totalFees → nested {admin:{value,text}, lockFee:{value,text}, totalFees:{value,text}}
		if (
			!adapted.fees ||
			typeof adapted.fees !== 'object' ||
			!adapted.fees.admin
		) {
			adapted.fees = {
				admin: formatPrice(adapted.adminFee || 0),
				lockFee: formatPrice(adapted.lockFee || 0),
				totalFees: formatPrice(adapted.totalFees || 0),
			};
		}

		// Phone: SSAG facilityPhone → phone
		if (!adapted.phone && adapted.facilityPhone) {
			adapted.phone = adapted.facilityPhone;
		}

		// Missing boolean fields — derive from SSAG data where possible.
		if (adapted.isOutOfNetwork == null) {
			adapted.isOutOfNetwork = adapted.inNetwork === false;
		}
		if (adapted.isSelfStorage == null) {
			adapted.isSelfStorage =
				adapted.facilityType === 'self-storage' ||
				get(adapted, 'corporation.type') === 'self-storage';
		}

		// unitCount: SSAG nests availability under units.unitsAvailable.
		// Read from the raw (pre-reshape) units object.
		if (adapted.unitCount == null) {
			const rawUnits =
				listing.units && !Array.isArray(listing.units)
					? listing.units
					: {};
			adapted.unitCount = parseInt(rawUnits.unitsAvailable, 10) || 0;
		}

		// cheapestPrice powers map pins, grid cards and SEO schema.
		// SSAG already computes the facility's lowest unit price.
		if (!adapted.cheapestPrice) {
			const lowest =
				listing.cheapestPrice != null
					? listing.cheapestPrice
					: listing.lowestPrice;
			adapted.cheapestPrice = formatPrice(lowest);
		}

		// Facility-level promotion string (SSAG: promotions.specialString).
		if (adapted.promotion == null) {
			adapted.promotion =
				get(listing, 'promotions.specialString', '') || '';
		}

		if (!adapted.messaging) {
			const amenities = listing.amenities || {};
			const promotions = listing.promotions || {};
			adapted.messaging = {
				isAaaEligible: !!promotions.hasAaaDiscount,
				isOpenNow: !!listing.isOpen,
				isOpenOrWillBe: !!listing.isOpenOrWillBe,
				isOpenOnMoveIn: !!(
					listing.isOpenOnMoveIn || listing.isOpenOnMid
				),
				isUrgent: Number(get(listing, 'reviews.number', 0)) >= 5,
				amexActive: !!amenities.amexActive,
				penskeRental: !!amenities.penskeRental,
				truckRental: !!amenities.truckRental,
				freeTruckRental: !!amenities.freeTruckRental,
				hasPenskeTruck: !!promotions.hasPenskeTruck,
			};
		}

		// cardType: frontend card treatment (SFAPIGW default facilityCard).
		if (!adapted.cardType) {
			adapted.cardType = 'facilityCard';
		}


		if (!Array.isArray(adapted.badges)) {
			adapted.badges = [];
		}
		if (adapted.hasOnlineMoveins && !adapted.badges.includes('omi')) {
			adapted.badges.push('omi');
		}
		if (
			adapted.hasContactlessMoveIns &&
			!adapted.badges.includes('contactless')
		) {
			adapted.badges.push('contactless');
		}

		if (adapted.location && typeof adapted.location === 'object') {
			const loc = adapted.location;
			const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
			adapted.location = {
				...loc,
				streetAddress: loc.streetAddress || loc.streetAddress1 || '',
				text:
					loc.text ||
					[
						loc.streetAddress1 || loc.streetAddress,
						cityState,
						loc.zip,
					]
						.filter(Boolean)
						.join(' '),
				country: loc.country || 'US',
				subLocality: loc.subLocality || '',
			};
		}

		return adapted;
	});

	const queryParams = data.query || {};
	const ssagLocation = data.location || {};

	const search = {
		searchId: data.searchId || null,
		facilityCollection: { collection: listings },
		totalFacilities: data.totalListings || data.totalFacilities || 0,
		numPages: data.totalPages || 0,
		facilitiesPerPage: data.listingsPerPage || 30,
		page: data.page || 1,
		searchType: data.searchType || 'storage',
		algorithm: data.algorithm || null,
		maxDistance: data.maxDistance || 0,
		todaysDate: data.todaysDate || null,
		filters: data.filters || { sqftRange: '' },
		location: {
			...ssagLocation,
			text:
				ssagLocation.text ||
				[ssagLocation.city, ssagLocation.state]
					.filter(Boolean)
					.join(', '),
			subPageType:
				ssagLocation.subPageType || queryParams.subPageType || '',
		},
		headlines: data.headlines || {
			primary: data.headline || '',
		},
		facets: data.facetCounts || {},
		staticMapUrl: data.staticMapUrl || null,
		lastInOriginalSearch: data.lastInOriginalSearch || null,
		totalUnits: data.totalUnits || 0,
		searchParams: queryParams,
		promoCounts: data.promoCounts || {},
		url: data.url || {
			state: data.stateUrl || '',
			city: data.cityUrl || '',
		},
		irmMarket: data.irmMarket || false,
		isTabbed: data.isTabbed || false,
		isBellhopsMarket: data.isBellhopsMarket || false,
		polygons: data.polygons || {},
		badgedFacilities: data.badgedFacilities || [],
		localPhone: data.phone || {},
	};

	const meta = data.meta || {};

	const seo = data.seo || {
		canonical: meta.canonical || '',
		canonicalMobile: '',
		canonicalWithQueryParams:
			meta.canonicalWithQueryParams || meta.canonical || '',
		prev: meta.prev || '',
		next: meta.next || '',
	};

	const rawContent = data.content || {};
	const content = {
		...rawContent,
		title: rawContent.contentTitle || rawContent.title || '',
		subtitle: rawContent.contentSubtitle || rawContent.subtitle || '',
		city: get(rawContent, 'location.city', ssagLocation.city || ''),
		state: get(rawContent, 'location.state', ssagLocation.state || ''),
		fullState: get(
			rawContent,
			'location.fullState',
			ssagLocation.fullState || '',
		),
		drillDownLinks: pickDrilldownLinks(
			data.drilldownLinks,
			rawContent.drilldownLinks,
			rawContent.drillDownLinks,
		),
		tpReviews: rawContent.tpReviews || null,
	};

	const location = {
		...ssagLocation,
		text:
			ssagLocation.text ||
			[ssagLocation.city, ssagLocation.state].filter(Boolean).join(', '),
		subPageType: ssagLocation.subPageType || queryParams.subPageType || '',
	};

	const phone = data.phone || {
		nationalSparephone: '',
		nationalSparephoneUnformatted: '',
	};

	return {
		search,
		meta,
		seo,
		content,
		location,
		phone,
		pageType: data.pageType,
		experiments: data.experiments,
	};
}

module.exports = adaptSSAGSearch;